// WorkOrdersTable.tsx — Sprint 11. The big table card: columns
// (status badge, vendor, costs, sign-off chip, action row), plus
// the expandable description/notes drawer. Stateless presentation —
// every row action is forwarded back to the orchestrator's handler
// surface.
import React from 'react';
import { Button, Card, Space, Table, Tag, Tooltip, Typography } from 'antd';
import {
  CheckCircleOutlined, DeleteOutlined, DollarOutlined, EditOutlined,
  SafetyCertificateOutlined, StopOutlined, ToolOutlined, UserOutlined,
} from '@ant-design/icons';
import { STATUS_COLORS, PRIORITY_COLORS, type WorkOrder } from './workOrderTypes';

import { formatDate } from '../../utils/date';
const { Text } = Typography;

export interface WorkOrdersTableProps {
  rows: WorkOrder[];

  onStart:        (order: WorkOrder) => void;
  onComplete:     (order: WorkOrder) => void;
  onCancel:       (order: WorkOrder) => void;
  onAssignVendor: (order: WorkOrder) => void;
  onUpdateCosts:  (order: WorkOrder) => void;
  onSignOff:      (order: WorkOrder) => void;
  onEdit:         (order: WorkOrder) => void;
  onDelete:       (order: WorkOrder) => void;
}

export const WorkOrdersTable: React.FC<WorkOrdersTableProps> = ({
  rows, onStart, onComplete, onCancel,
  onAssignVendor, onUpdateCosts, onSignOff,
  onEdit, onDelete,
}) => {
  const columns = [
    {
      title: 'Title', dataIndex: 'title', key: 'title',
      render: (title: string, record: WorkOrder) => (
        <div>
          <Text strong style={{ fontSize: 14 }}>{title}</Text>
          {record.unitNumber && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Unit {record.unitNumber}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 130,
      render: (status: string) => (
        <Tag color={STATUS_COLORS[status] || 'default'}>{status.replace('_', ' ')}</Tag>
      ),
    },
    {
      title: 'Priority', dataIndex: 'priority', key: 'priority', width: 110,
      render: (priority: string) =>
        priority
          ? <Tag color={PRIORITY_COLORS[priority] || 'default'}>{priority}</Tag>
          : <Text type="secondary">—</Text>,
    },
    {
      title: 'Vendor', dataIndex: 'vendorName', key: 'vendorName',
      render: (vendor: string, record: WorkOrder) =>
        vendor ? (
          <div>
            <Text>{vendor}</Text>
            {record.vendorContact && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>{record.vendorContact}</Text>
              </div>
            )}
          </div>
        ) : <Text type="secondary">Unassigned</Text>,
    },
    {
      title: 'Scheduled', dataIndex: 'scheduledDate', key: 'scheduledDate',
      render: (date: string) =>
        date ? formatDate(date) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Est. Cost', dataIndex: 'estimatedCost', key: 'estimatedCost',
      render: (cost: number) =>
        cost != null ? `$${cost.toLocaleString()}` : <Text type="secondary">—</Text>,
    },
    {
      title: 'Actual Cost', key: 'actualCost', width: 140,
      render: (_: any, record: WorkOrder) => {
        const r = record as any;
        if (r.laborCost != null || r.materialCost != null) {
          return (
            <div>
              <Text strong>
                ${(Number(r.laborCost || 0) + Number(r.materialCost || 0)).toLocaleString()}
              </Text>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  L: ${Number(r.laborCost || 0).toLocaleString()} / M: ${Number(r.materialCost || 0).toLocaleString()}
                </Text>
              </div>
              {r.invoiceNumber && (
                <Text type="secondary" style={{ fontSize: 11 }}>Inv# {r.invoiceNumber}</Text>
              )}
            </div>
          );
        }
        if (record.actualCost != null) return `$${Number(record.actualCost).toLocaleString()}`;
        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Sign-off', key: 'signoff', width: 120,
      render: (_: any, record: WorkOrder) => {
        const r = record as any;
        if (r.signedOffBy) {
          return (
            <div>
              <Tag color="green" icon={<SafetyCertificateOutlined />} style={{ fontSize: 11 }}>Signed</Tag>
              <div><Text type="secondary" style={{ fontSize: 11 }}>{r.signedOffBy}</Text></div>
            </div>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Actions', key: 'actions', width: 230,
      render: (_: any, record: WorkOrder) => (
        <Space size={4} wrap>
          {record.status === 'OPEN' && (
            <Tooltip title="Mark In Progress">
              <Button size="small" icon={<ToolOutlined />} onClick={() => onStart(record)}>Start</Button>
            </Tooltip>
          )}
          {record.status === 'IN_PROGRESS' && (
            <Tooltip title="Mark as Completed">
              <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => onComplete(record)}>
                Complete
              </Button>
            </Tooltip>
          )}
          {(record.status === 'OPEN' || record.status === 'IN_PROGRESS') && (
            <Tooltip title="Assign Vendor">
              <Button size="small" icon={<UserOutlined />} onClick={() => onAssignVendor(record)} />
            </Tooltip>
          )}
          {(record.status === 'IN_PROGRESS' || record.status === 'COMPLETED') && (
            <Tooltip title="Update Costs">
              <Button size="small" icon={<DollarOutlined />} onClick={() => onUpdateCosts(record)} />
            </Tooltip>
          )}
          {record.status === 'COMPLETED' && !(record as any).signedOffBy && (
            <Tooltip title="Sign Off">
              <Button size="small" type="dashed" icon={<SafetyCertificateOutlined />} onClick={() => onSignOff(record)} />
            </Tooltip>
          )}
          {(record.status === 'OPEN' || record.status === 'IN_PROGRESS') && (
            <Tooltip title="Cancel work order">
              <Button size="small" danger icon={<StopOutlined />} onClick={() => onCancel(record)} />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card bordered={false} style={{ borderRadius: 10 }}>
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        expandable={{
          expandedRowRender: (record: WorkOrder) => (
            <div style={{ padding: '8px 16px', background: '#fafafa', borderRadius: 8 }}>
              {record.description && (
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    Description
                  </Text>
                  <div><Text>{record.description}</Text></div>
                </div>
              )}
              {record.notes && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    Notes
                  </Text>
                  <div><Text>{record.notes}</Text></div>
                </div>
              )}
              {record.maintenanceRequestId && (
                <div style={{ marginTop: 8 }}>
                  <Tag color="purple" style={{ fontSize: 12 }}>
                    Linked to Maintenance Request #{record.maintenanceRequestId.slice(0, 8)}…
                  </Tag>
                </div>
              )}
              {record.completedDate && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Completed: {formatDate(record.completedDate)}
                  </Text>
                </div>
              )}
            </div>
          ),
          rowExpandable: (record: WorkOrder) => !!(
            record.description || record.notes || record.maintenanceRequestId || record.completedDate
          ),
        }}
      />
    </Card>
  );
};
